import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { TimeInputOutput } from '@api/models/settings/TimeInputOutput';
import { TimeInputOutputUnit } from '@api/models/enums/TimeInputOutputUnit';
import { ConfirmByTypingModal } from '../common/ConfirmByTypingModal';

interface DeletionConfirmationModalProps {
  open: boolean;
  entryRecordsDeletion?: TimeInputOutput | null;
  trainingDeletion?: TimeInputOutput | null;
  certificateDeletion?: TimeInputOutput | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const CONFIRMATION_TEXT = 'AGREE';

export const DeletionConfirmationModal: FC<DeletionConfirmationModalProps> = ({
  open,
  entryRecordsDeletion,
  trainingDeletion,
  certificateDeletion,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  const formatTimeUnit = (unit: TimeInputOutputUnit, count: number): string => {
    const key = `qcsCompanySettings.deletionConfirmation.units.${unit.toLowerCase()}`;
    return t(key, { count });
  };

  return (
    <ConfirmByTypingModal
      open={open}
      title={t('qcsCompanySettings.deletionConfirmation.title')}
      instruction={t('qcsCompanySettings.deletionConfirmation.instructionText', { text: CONFIRMATION_TEXT })}
      confirmationText={CONFIRMATION_TEXT}
      confirmLabel={t('qcsCompanySettings.deletionConfirmation.confirm')}
      cancelLabel={t('qcsCompanySettings.deletionConfirmation.cancel')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t('qcsCompanySettings.deletionConfirmation.message')}
      </Typography>

      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
        {entryRecordsDeletion && (
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <Trans
              i18nKey="qcsCompanySettings.deletionConfirmation.entryRecords"
              values={{
                value: entryRecordsDeletion.value,
                unit: formatTimeUnit(entryRecordsDeletion.unit, entryRecordsDeletion.value),
              }}
              components={{ bold: <Box component="span" sx={{ fontWeight: 700 }} /> }}
            />
          </Typography>
        )}

        {trainingDeletion && (
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <Trans
              i18nKey="qcsCompanySettings.deletionConfirmation.trainingRecords"
              values={{
                value: trainingDeletion.value,
                unit: formatTimeUnit(trainingDeletion.unit, trainingDeletion.value),
              }}
              components={{ bold: <Box component="span" sx={{ fontWeight: 700 }} /> }}
            />
          </Typography>
        )}

        {certificateDeletion && (
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <Trans
              i18nKey="qcsCompanySettings.deletionConfirmation.certificateRecords"
              values={{
                value: certificateDeletion.value,
                unit: formatTimeUnit(certificateDeletion.unit, certificateDeletion.value),
              }}
              components={{ bold: <Box component="span" sx={{ fontWeight: 700 }} /> }}
            />
          </Typography>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
        {t('qcsCompanySettings.deletionConfirmation.warning')}
      </Typography>
    </ConfirmByTypingModal>
  );
};
